import { useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApiQuery } from '@/hooks/useApiQuery';
import { getFileUrl } from '@/utils/fileUrl';
import DocumentDetailModal from '@/pages/documents/components/DocumentDetailModal';
import DeleteConfirmModal from '@/components/base/DeleteConfirmModal';
import EditUserModal from '@/pages/users/components/EditUserModal';
import AddUserModal from '@/pages/users/components/AddUserModal';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/contexts/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || 'https://ekyc.tumkurconnect.com/api/v1';

interface Company {
  id: number;
  name: string;
  slug: string;
  logoUrl: string | null;
  contactEmail: string;
  contactPhone: string;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postalCode: string | null;
  website: string | null;
  primaryColor: string;
  secondaryColor: string;
  isActive: boolean;
  totalUsers: number;
  totalCustomers: number;
  createdAt: string;
  updatedAt: string;
}

interface User {
  id: number;
  email: string;
  full_name: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  role: string;
  role_id: number;
  is_active: number;
  is_deleted: number;
  deleted_at: string | null;
  last_login: string | null;
  created_at: string;
  avatar_url: string | null;
  company_id: number | null;
}

interface Customer {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  idType: string | null;
  idNumber: string | null;
  customerType: string;
  status: string;
  riskLevel: string;
  isDeleted: number;
  deletedAt: string | null;
  createdAt: string;
}

interface DocItem {
  id: number;
  fileName: string;
  docType: string;
  docCategory: string;
  fileSize: number;
  mimeType: string;
  verificationStatus: string;
  customer_name: string;
  uploaded_by_name: string;
  reviewed_by_name: string | null;
  reviewReason: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

type TabKey = 'overview' | 'users' | 'customers' | 'documents';

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const roleColors: Record<string, string> = {
  superadmin: 'bg-violet-50 text-violet-700',
  company_admin: 'bg-brand-50 text-brand-700',
  kyc_officer: 'bg-sky-50 text-sky-700',
  customer: 'bg-stone-100 text-stone-700',
};

const riskColors: Record<string, string> = {
  low: 'bg-emerald-50 text-emerald-700',
  medium: 'bg-amber-50 text-amber-700',
  high: 'bg-red-50 text-red-700',
};

const typeColors: Record<string, string> = {
  individual: 'bg-sky-50 text-sky-700',
  sole_proprietor: 'bg-violet-50 text-violet-700',
  private_limited: 'bg-teal-50 text-teal-700',
  public_limited: 'bg-orange-50 text-orange-700',
  llp: 'bg-rose-50 text-rose-700',
  partnership: 'bg-indigo-50 text-indigo-700',
  trust: 'bg-amber-50 text-amber-700',
  ngo: 'bg-emerald-50 text-emerald-700',
  others: 'bg-stone-100 text-stone-600',
};

const CUSTOMER_TYPE_LABELS: Record<string, string> = {
  individual: 'Individual',
  sole_proprietor: 'Sole Proprietor',
  private_limited: 'Private Limited',
  public_limited: 'Public Limited',
  llp: 'LLP',
  partnership: 'Partnership',
  trust: 'Trust',
  ngo: 'NGO',
  others: 'Others',
};

const statusColors: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700',
  verified: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-red-50 text-red-700',
};

export default function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const companyId = Number(id);
  const { user: currentUser } = useAuth();
  const { put, del } = useApi();
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailDoc, setDetailDoc] = useState<number | null>(null);

  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [toggleLoadingId, setToggleLoadingId] = useState<number | null>(null);

  // Company customers tab: multi-select + bulk transfer
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<Set<number>>(new Set());
  const [bulkTransferOpen, setBulkTransferOpen] = useState(false);
  const [bulkTargetCompanyId, setBulkTargetCompanyId] = useState('');
  const [bulkTransferLoading, setBulkTransferLoading] = useState(false);
  const [bulkTransferError, setBulkTransferError] = useState<string | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string; mode: 'soft' | 'hard' } | null>(null);
  const [showDeletedUsers, setShowDeletedUsers] = useState(false);
  const [userDeleteTarget, setUserDeleteTarget] = useState<{ id: number; name: string; mode: 'soft' | 'hard' } | null>(null);

  const canManageCompanyUsers = currentUser?.role === 'superadmin' ||
    (currentUser?.role === 'company_admin' && currentUser?.company?.id === companyId);
  const isSuperAdmin = currentUser?.role === 'superadmin';

  // Fetch all companies for bulk transfer dropdown
  const { data: allCompaniesData } = useApiQuery<{ companies?: { id: number; name: string }[] }>({
    endpoint: '/companies',
    enabled: isSuperAdmin && activeTab === 'customers',
  });
  const allCompaniesList = allCompaniesData?.companies || [];

  const { data: company, loading: companyLoading, error: companyError } = useApiQuery<Company>({
    endpoint: `/companies/${companyId}`,
    enabled: !!id && !isNaN(companyId),
  });

  const { data: users, loading: usersLoading, refetch: refetchUsers } = useApiQuery<{ data: User[] } | User[]>({
    endpoint: `/users?companyId=${companyId}${showDeletedUsers ? '&showDeleted=true' : ''}`,
    enabled: !!id && !isNaN(companyId) && activeTab === 'users',
  });

  const { data: customers, loading: customersLoading, refetch: refetchCustomers } = useApiQuery<{ data: Customer[] } | Customer[]>({
    endpoint: `/customers?companyId=${companyId}${showDeleted ? '&showDeleted=true' : ''}`,
    enabled: !!id && !isNaN(companyId) && activeTab === 'customers',
  });

  const { data: documents, loading: documentsLoading } = useApiQuery<{ data: DocItem[] } | DocItem[]>({
    endpoint: `/documents?companyId=${companyId}`,
    enabled: !!id && !isNaN(companyId) && activeTab === 'documents',
  });

  const handleBulkTransfer = useCallback(async () => {
    if (!bulkTargetCompanyId || selectedCustomerIds.size === 0) return;
    setBulkTransferLoading(true);
    setBulkTransferError(null);
    try {
      const results = await Promise.all(
        Array.from(selectedCustomerIds).map(async (custId) => {
          try {
            await put(`/customers/${custId}`, { companyId: Number(bulkTargetCompanyId) });
            return { id: custId, ok: true };
          } catch (err) {
            return { id: custId, ok: false, error: err instanceof Error ? err.message : 'Failed' };
          }
        })
      );
      const failed = results.filter((r) => !r.ok);
      if (failed.length > 0) {
        setBulkTransferError(`${failed.length} of ${results.length} transfers failed. Please check permissions.`);
        setBulkTransferLoading(false);
        return;
      }
      setBulkTransferOpen(false);
      setBulkTargetCompanyId('');
      setSelectedCustomerIds(new Set());
      refetchCustomers();
    } catch (err) {
      setBulkTransferError(err instanceof Error ? err.message : 'Bulk transfer failed');
    } finally {
      setBulkTransferLoading(false);
    }
  }, [bulkTargetCompanyId, selectedCustomerIds, put, refetchCustomers]);

  const handleSoftDelete = useCallback(async () => {
    if (!deleteTarget || deleteTarget.mode !== 'soft') return;
    await put(`/customers/${deleteTarget.id}/soft-delete`, {});
    setDeleteTarget(null);
    refetchCustomers();
  }, [deleteTarget, put, refetchCustomers]);

  const handleHardDelete = useCallback(async () => {
    if (!deleteTarget || deleteTarget.mode !== 'hard') return;
    await del(`/customers/${deleteTarget.id}`);
    setDeleteTarget(null);
    refetchCustomers();
  }, [deleteTarget, del, refetchCustomers]);

  const handleRestore = useCallback(async (id: number) => {
    await put(`/customers/${id}/restore`, {});
    refetchCustomers();
  }, [put, refetchCustomers]);

  // User delete handlers — MUST be declared before any early returns
  const handleUserSoftDelete = useCallback(async () => {
    if (!userDeleteTarget || userDeleteTarget.mode !== 'soft') return;
    await put(`/users/${userDeleteTarget.id}/soft-delete`, {});
    setUserDeleteTarget(null);
    refetchUsers();
  }, [userDeleteTarget, put, refetchUsers]);

  const handleUserHardDelete = useCallback(async () => {
    if (!userDeleteTarget || userDeleteTarget.mode !== 'hard') return;
    await del(`/users/${userDeleteTarget.id}`);
    setUserDeleteTarget(null);
    refetchUsers();
  }, [userDeleteTarget, del, refetchUsers]);

  const handleUserRestore = useCallback(async (id: number) => {
    await put(`/users/${id}/restore`, {});
    refetchUsers();
  }, [put, refetchUsers]);

  if (companyLoading) {
    return (
      <div className="p-4 md:p-8 min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line text-brand-600 text-2xl animate-spin w-8 h-8 flex items-center justify-center" />
          <span className="text-stone-500 text-sm">Loading company details...</span>
        </div>
      </div>
    );
  }

  if (companyError || !company) {
    return (
      <div className="p-4 md:p-8 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="ri-building-4-line text-stone-300 text-3xl mb-2 w-12 h-12 flex items-center justify-center mx-auto" />
          <p className="text-stone-500 text-sm">{companyError || 'Company not found.'}</p>
          <Link to="/companies" className="text-brand-600 text-sm mt-3 inline-block hover:underline">
            Back to Companies
          </Link>
        </div>
      </div>
    );
  }

  const tabs: { key: TabKey; label: string; count?: number }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'users', label: 'Users', count: company.totalUsers },
    { key: 'customers', label: 'Customers', count: company.totalCustomers },
    { key: 'documents', label: 'Documents' },
  ];

  // Normalize array responses
  const userList = Array.isArray(users) ? users : (users as { data?: User[] } | null)?.data || [];
  const customerList = Array.isArray(customers) ? customers : (customers as { data?: Customer[] } | null)?.data || [];
  const documentList = Array.isArray(documents) ? documents : (documents as { data?: DocItem[] } | null)?.data || [];

  // Filtering
  const filteredUsers = userList.filter((u) => {
    const q = search.toLowerCase();
    const matchesSearch = (u.full_name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.phone || '').toLowerCase().includes(q);
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' ? u.is_active : !u.is_active);
    return matchesSearch && matchesRole && matchesStatus;
  });

  const filteredCustomers = customerList.filter((c) => {
    const q = search.toLowerCase();
    return (c.firstName || '').toLowerCase().includes(q) ||
      (c.lastName || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.idNumber || '').toLowerCase().includes(q);
  });

  const filteredDocs = documentList.filter((d) => {
    const q = search.toLowerCase();
    const matchesSearch = (d.fileName || '').toLowerCase().includes(q) ||
      (d.customer_name || '').toLowerCase().includes(q) ||
      (d.docType || '').toLowerCase().includes(q);
    const matchesCategory = categoryFilter === 'all' || d.docCategory === categoryFilter;
    const matchesStatus = statusFilter === 'all' || d.verificationStatus === statusFilter;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  return (
    <div className="p-4 md:p-8 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Link
            to="/companies"
            className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-500 transition-colors"
          >
            <i className="ri-arrow-left-line text-lg w-5 h-5 flex items-center justify-center" />
          </Link>
          <div className="w-11 h-11 rounded-xl bg-brand-100 flex items-center justify-center shrink-0 overflow-hidden">
            {company.logoUrl ? (
              <img src={getFileUrl(company.logoUrl)} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-brand-700 font-bold text-sm">{company.name.charAt(0)}</span>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-stone-900">{company.name}</h1>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${company.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                {company.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <p className="text-stone-500 text-sm mt-0.5">{company.slug} · {company.contactEmail}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-stone-100 p-1 mb-6 inline-flex gap-1">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => { setActiveTab(t.key); setSearch(''); setCategoryFilter('all'); setRoleFilter('all'); setStatusFilter('all'); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === t.key
                ? 'bg-brand-600 text-white'
                : 'text-stone-600 hover:bg-stone-50'
            }`}
          >
            {t.label}
            {typeof t.count === 'number' && (
              <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${activeTab === t.key ? 'bg-white/20 text-white' : 'bg-stone-200 text-stone-600'}`}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Overview */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Company Info */}
            <div className="bg-white rounded-xl border border-stone-100 p-6">
              <h3 className="font-bold text-stone-900 mb-4">Company Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Company Name</p>
                  <p className="text-stone-900 text-sm font-medium">{company.name}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Slug</p>
                  <p className="text-stone-900 text-sm">{company.slug}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Contact Email</p>
                  <p className="text-stone-900 text-sm">{company.contactEmail || '-'}</p>
                </div>
                <div>
                  <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Contact Phone</p>
                  <p className="text-stone-900 text-sm">{company.contactPhone || '-'}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Address</p>
                  <p className="text-stone-900 text-sm">
                    {company.address
                      ? `${company.address}${company.city ? `, ${company.city}` : ''}${company.state ? `, ${company.state}` : ''}${company.country ? `, ${company.country}` : ''} ${company.postalCode || ''}`
                      : '-'}
                  </p>
                </div>
                {company.website && (
                  <div>
                    <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Website</p>
                    <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-brand-600 text-sm hover:underline">
                      {company.website}
                    </a>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Branding Colors</p>
                  <div className="flex items-center gap-3 mt-1">
                    <div className="flex items-center gap-1.5">
                      <span className="w-5 h-5 rounded-md border border-stone-200 inline-block" style={{ backgroundColor: company.primaryColor }} />
                      <span className="text-stone-700 text-xs font-mono">{company.primaryColor}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-5 h-5 rounded-md border border-stone-200 inline-block" style={{ backgroundColor: company.secondaryColor }} />
                      <span className="text-stone-700 text-xs font-mono">{company.secondaryColor}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-stone-100 p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center">
                    <i className="ri-group-line text-sky-600 text-lg w-5 h-5 flex items-center justify-center" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-stone-900">{company.totalUsers}</p>
                    <p className="text-stone-400 text-xs">Users</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-stone-100 p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <i className="ri-user-line text-emerald-600 text-lg w-5 h-5 flex items-center justify-center" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-stone-900">{company.totalCustomers}</p>
                    <p className="text-stone-400 text-xs">Customers</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-stone-100 p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                    <i className="ri-calendar-line text-amber-600 text-lg w-5 h-5 flex items-center justify-center" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-stone-900">{new Date(company.createdAt).toLocaleDateString()}</p>
                    <p className="text-stone-400 text-xs">Created</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Side Card */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-stone-100 p-5">
              <h4 className="font-bold text-stone-900 text-sm mb-3">Status Overview</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-stone-500 text-xs">Status</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${company.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                    {company.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-stone-500 text-xs">Users</span>
                  <span className="text-stone-900 text-xs font-medium">{company.totalUsers}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-stone-500 text-xs">Customers</span>
                  <span className="text-stone-900 text-xs font-medium">{company.totalCustomers}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-stone-500 text-xs">Created</span>
                  <span className="text-stone-900 text-xs">{new Date(company.createdAt).toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-stone-500 text-xs">Updated</span>
                  <span className="text-stone-900 text-xs">{new Date(company.updatedAt).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          {/* Filters & Add button */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="bg-white rounded-xl border border-stone-100 p-4 flex flex-col sm:flex-row gap-3 flex-1 w-full items-center">
              <div className="relative flex-1 max-w-md">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search users..."
                  className="w-full border border-stone-200 rounded-lg pl-10 pr-4 py-2 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                />
              </div>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-900 focus:outline-none focus:border-brand-400"
              >
                <option value="all">All Roles</option>
                <option value="company_admin">Company Admin</option>
                <option value="kyc_officer">KYC Officer</option>
                <option value="customer">Customer</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-900 focus:outline-none focus:border-brand-400"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={showDeletedUsers}
                  onChange={(e) => setShowDeletedUsers(e.target.checked)}
                />
                <div className="w-9 h-5 bg-stone-300 peer-checked:bg-brand-500 rounded-full transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-4" />
                <span className="text-stone-600 text-sm whitespace-nowrap">Show deleted</span>
              </label>
            </div>
            {canManageCompanyUsers && (
              <button
                onClick={() => setAddUserOpen(true)}
                className="bg-brand-600 hover:bg-brand-700 text-white font-semibold px-4 py-2 rounded-full text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer shrink-0"
              >
                <i className="ri-add-line" />
                Add User
              </button>
            )}
          </div>

          <div className="bg-white rounded-xl border border-stone-100 overflow-hidden">
            {usersLoading ? (
              <div className="p-8 text-center">
                <i className="ri-loader-4-line text-brand-600 text-2xl animate-spin w-8 h-8 flex items-center justify-center mx-auto" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-stone-50 border-b border-stone-100">
                    <tr>
                      <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">User</th>
                      <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Role</th>
                      <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Phone</th>
                      <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Last Login</th>
                      <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-stone-400 text-sm">
                          No users found.
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((u) => (
                        <tr key={u.id} className={`hover:bg-stone-50 transition-colors ${u.is_deleted ? 'bg-stone-50/60' : ''}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 overflow-hidden ${u.is_deleted ? 'bg-stone-200' : 'bg-brand-100'}`}>
                                {u.avatar_url ? (
                                  <img src={getFileUrl(u.avatar_url)} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <span className={`font-bold text-xs ${u.is_deleted ? 'text-stone-400' : 'text-brand-700'}`}>
                                    {(u.first_name?.charAt(0) || '')}{u.last_name?.charAt(0) || ''}
                                  </span>
                                )}
                              </div>
                              <div>
                                <p className={`text-sm font-medium ${u.is_deleted ? 'text-stone-400 line-through' : 'text-stone-900'}`}>{u.full_name}</p>
                                <p className="text-stone-400 text-xs">{u.email}</p>
                                {u.is_deleted && u.deleted_at && (
                                  <p className="text-red-400 text-[10px]">Deleted {new Date(u.deleted_at).toLocaleDateString()}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleColors[u.role] || 'bg-stone-100 text-stone-700'}`}>
                              {u.role.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">{u.phone || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              u.is_deleted ? 'bg-red-50 text-red-700' :
                              u.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                            }`}>
                              {u.is_deleted ? 'Deleted' : u.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-stone-500 text-sm">{u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {canManageCompanyUsers && !u.is_deleted && (
                                <>
                                  <button
                                    onClick={() => setEditingUser(u)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-500 hover:text-stone-700 transition-colors"
                                    title="Edit user"
                                  >
                                    <i className="ri-edit-line text-sm" />
                                  </button>
                                  <button
                                    onClick={async () => {
                                      setToggleLoadingId(u.id);
                                      try {
                                        await put(`/users/${u.id}`, { isActive: !u.is_active });
                                        refetchUsers();
                                      } catch {
                                        // silently fail
                                      } finally {
                                        setToggleLoadingId(null);
                                      }
                                    }}
                                    disabled={toggleLoadingId === u.id}
                                    className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                                      u.is_active
                                        ? 'hover:bg-red-50 text-stone-500 hover:text-red-600'
                                        : 'hover:bg-emerald-50 text-stone-500 hover:text-emerald-600'
                                    }`}
                                    title={u.is_active ? 'Deactivate user' : 'Activate user'}
                                  >
                                    {toggleLoadingId === u.id ? (
                                      <i className="ri-loader-4-line animate-spin text-xs" />
                                    ) : u.is_active ? (
                                      <i className="ri-close-circle-line text-sm" />
                                    ) : (
                                      <i className="ri-check-line text-sm" />
                                    )}
                                  </button>
                                  <button
                                    onClick={() => setUserDeleteTarget({ id: u.id, name: u.full_name, mode: 'soft' })}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-500 hover:text-red-600 transition-colors"
                                    title="Deactivate"
                                  >
                                    <i className="ri-delete-bin-line text-sm" />
                                  </button>
                                </>
                              )}
                              {canManageCompanyUsers && u.is_deleted && (
                                <>
                                  <button
                                    onClick={() => handleUserRestore(u.id)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-emerald-50 text-stone-500 hover:text-emerald-600 transition-colors"
                                    title="Reactivate"
                                  >
                                    <i className="ri-arrow-go-back-line text-sm" />
                                  </button>
                                  <button
                                    onClick={() => setUserDeleteTarget({ id: u.id, name: u.full_name, mode: 'hard' })}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-stone-500 hover:text-red-600 transition-colors"
                                    title="Permanently delete"
                                  >
                                    <i className="ri-fire-line text-sm" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Customers Tab */}
      {activeTab === 'customers' && (
        <div className="space-y-4">
          {/* Search + bulk bar + show deleted */}
          <div className="bg-white rounded-xl border border-stone-100 p-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search customers..."
                className="w-full border border-stone-200 rounded-lg pl-10 pr-4 py-2 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
              />
            </div>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={showDeleted}
                  onChange={(e) => setShowDeleted(e.target.checked)}
                />
                <div className="w-9 h-5 bg-stone-300 peer-checked:bg-brand-500 rounded-full transition-colors relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-4" />
                <span className="text-stone-600 text-sm">Show deleted</span>
              </label>
              {selectedCustomerIds.size > 0 && (
                <div className="flex items-center gap-2">
                  {isSuperAdmin && (
                    <button
                      onClick={() => { setBulkTransferOpen(true); setBulkTransferError(null); setBulkTargetCompanyId(''); }}
                      className="bg-white border border-brand-200 hover:bg-brand-100 text-brand-700 font-semibold px-4 py-2 rounded-full text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-exchange-line" />
                      Transfer Company
                    </button>
                  )}
                  <button
                    onClick={() => setSelectedCustomerIds(new Set())}
                    className="text-stone-500 hover:text-stone-700 text-xs font-medium px-3 py-2 rounded-full hover:bg-stone-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Selection bar */}
          {selectedCustomerIds.size > 0 && (
            <div className="bg-brand-50 border border-brand-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-brand-100 text-brand-700 text-xs font-bold flex items-center justify-center">
                  {selectedCustomerIds.size}
                </span>
                <span className="text-sm font-medium text-brand-800">
                  {selectedCustomerIds.size} customer{selectedCustomerIds.size > 1 ? 's' : ''} selected
                </span>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-stone-100 overflow-hidden">
            {customersLoading ? (
              <div className="p-8 text-center">
                <i className="ri-loader-4-line text-brand-600 text-2xl animate-spin w-8 h-8 flex items-center justify-center mx-auto" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-stone-50 border-b border-stone-100">
                    <tr>
                      <th className="px-3 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={!!filteredCustomers && filteredCustomers.length > 0 && filteredCustomers.every((c) => selectedCustomerIds.has(c.id))}
                          onChange={() => {
                            if (!filteredCustomers) return;
                            const visibleIds = filteredCustomers.map((c) => c.id);
                            const allSelected = visibleIds.every((id) => selectedCustomerIds.has(id));
                            if (allSelected) {
                              setSelectedCustomerIds((prev) => {
                                const next = new Set(prev);
                                visibleIds.forEach((vid) => next.delete(vid));
                                return next;
                              });
                            } else {
                              setSelectedCustomerIds((prev) => {
                                const next = new Set(prev);
                                visibleIds.forEach((vid) => next.add(vid));
                                return next;
                              });
                            }
                          }}
                          className="w-4 h-4 rounded border-stone-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                        />
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Customer</th>
                      <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">ID Document</th>
                      <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Risk Level</th>
                      <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {filteredCustomers.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-stone-400 text-sm">
                          No customers found.
                        </td>
                      </tr>
                    ) : (
                      filteredCustomers.map((c) => (
                        <tr key={c.id} className={`hover:bg-stone-50 transition-colors ${c.isDeleted ? 'bg-stone-50/60' : ''} ${selectedCustomerIds.has(c.id) ? 'bg-brand-50/40' : ''}`}>
                          <td className="px-3 py-3">
                            <input
                              type="checkbox"
                              checked={selectedCustomerIds.has(c.id)}
                              onChange={() => {
                                setSelectedCustomerIds((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(c.id)) next.delete(c.id);
                                  else next.add(c.id);
                                  return next;
                                });
                              }}
                              className="w-4 h-4 rounded border-stone-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${c.isDeleted ? 'bg-stone-200' : 'bg-stone-100'}`}>
                                <span className={`font-bold text-xs ${c.isDeleted ? 'text-stone-400' : 'text-stone-500'}`}>
                                  {c.firstName.charAt(0)}{c.lastName.charAt(0)}
                                </span>
                              </div>
                              <div>
                                <p className={`text-sm font-medium ${c.isDeleted ? 'text-stone-400 line-through' : 'text-stone-900'}`}>
                                  {c.firstName} {c.lastName}
                                </p>
                                <p className="text-stone-400 text-xs">{c.email}</p>
                                {c.isDeleted && c.deletedAt && (
                                  <p className="text-red-400 text-[10px]">Deleted {new Date(c.deletedAt).toLocaleDateString()}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeColors[c.customerType] || 'bg-stone-100 text-stone-600'}`}>
                              {CUSTOMER_TYPE_LABELS[c.customerType] || c.customerType}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <p className={`text-sm ${c.isDeleted ? 'text-stone-400' : 'text-stone-700'}`}>{c.idType || '-'}</p>
                            <p className="text-stone-400 text-xs">{c.idNumber || '-'}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${riskColors[c.riskLevel] || 'bg-stone-100 text-stone-700'}`}>
                              {c.riskLevel}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              c.isDeleted ? 'bg-red-50 text-red-700' :
                              c.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                            }`}>
                              {c.isDeleted ? 'Deleted' : c.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Link
                                to={`/customers/${c.id}`}
                                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-brand-600 transition-colors cursor-pointer"
                                title="View"
                              >
                                <i className="ri-eye-line text-sm w-4 h-4 flex items-center justify-center" />
                              </Link>
                              {!c.isDeleted && (
                                <button
                                  onClick={() => setDeleteTarget({ id: c.id, name: `${c.firstName} ${c.lastName}`, mode: 'soft' })}
                                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-red-600 transition-colors cursor-pointer"
                                  title="Deactivate"
                                >
                                  <i className="ri-delete-bin-line text-sm w-4 h-4 flex items-center justify-center" />
                                </button>
                              )}
                              {c.isDeleted && (
                                <>
                                  <button
                                    onClick={() => handleRestore(c.id)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-emerald-50 text-stone-400 hover:text-emerald-600 transition-colors cursor-pointer"
                                    title="Reactivate"
                                  >
                                    <i className="ri-arrow-go-back-line text-sm w-4 h-4 flex items-center justify-center" />
                                  </button>
                                  <button
                                    onClick={() => setDeleteTarget({ id: c.id, name: `${c.firstName} ${c.lastName}`, mode: 'hard' })}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-600 transition-colors cursor-pointer"
                                    title="Permanently delete"
                                  >
                                    <i className="ri-fire-line text-sm w-4 h-4 flex items-center justify-center" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-xl border border-stone-100 p-4 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search documents..."
                className="w-full border border-stone-200 rounded-lg pl-10 pr-4 py-2 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-900 focus:outline-none focus:border-brand-400"
            >
              <option value="all">All Categories</option>
              <option value="identity">Identity</option>
              <option value="address">Address</option>
              <option value="photo">Photo</option>
              <option value="signature">Signature</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-900 focus:outline-none focus:border-brand-400"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="verified">Verified</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {documentsLoading ? (
            <div className="p-8 text-center">
              <i className="ri-loader-4-line text-brand-600 text-2xl animate-spin w-8 h-8 flex items-center justify-center mx-auto" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredDocs.length === 0 ? (
                <div className="col-span-full text-center py-8">
                  <i className="ri-file-list-3-line text-stone-300 text-3xl mb-2 w-12 h-12 flex items-center justify-center mx-auto" />
                  <p className="text-stone-400 text-sm">No documents found.</p>
                </div>
              ) : (
                filteredDocs.map((doc) => (
                  <div key={doc.id} className="bg-white rounded-xl border border-stone-100 p-5 hover:border-brand-200 transition-colors cursor-pointer" onClick={() => setDetailDoc(doc.id)}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-lg bg-stone-100 flex items-center justify-center">
                        <i className="ri-file-line text-stone-400 text-xl w-5 h-5 flex items-center justify-center" />
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[doc.verificationStatus] || 'bg-stone-100 text-stone-700'}`}>
                        {doc.verificationStatus}
                      </span>
                    </div>
                    <p className="text-stone-900 text-sm font-medium truncate mb-1">{doc.fileName}</p>
                    <p className="text-stone-400 text-xs mb-1">{doc.docType} · {formatSize(doc.fileSize)}</p>
                    <p className="text-stone-400 text-[10px] mb-3">{doc.customer_name}</p>
                    {doc.verificationStatus === 'failed' && doc.reviewReason && (
                      <div className="mb-3 bg-red-50 border border-red-100 rounded-lg px-2.5 py-1.5">
                        <p className="text-red-600 text-[10px] leading-tight">
                          <span className="font-semibold">Disapproved:</span> {doc.reviewReason}
                        </p>
                      </div>
                    )}
                    {doc.verificationStatus === 'verified' && doc.reviewed_by_name && (
                      <div className="mb-3 bg-emerald-50 border border-emerald-100 rounded-lg px-2.5 py-1.5">
                        <p className="text-emerald-600 text-[10px] leading-tight">
                          <span className="font-semibold">Approved by:</span> {doc.reviewed_by_name}
                          {doc.reviewedAt && ` · ${new Date(doc.reviewedAt).toLocaleDateString()}`}
                        </p>
                      </div>
                    )}
                    <div className="flex items-center gap-2 pt-3 border-t border-stone-50" onClick={(e) => e.stopPropagation()}>
                      <a
                        href={`${API_BASE}/documents/${doc.id}/download?inline=true`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 py-2 rounded-lg bg-stone-50 hover:bg-stone-100 text-stone-600 text-xs font-medium transition-colors text-center"
                      >
                        View
                      </a>
                      <a
                        href={`${API_BASE}/documents/${doc.id}/download`}
                        className="flex-1 py-2 rounded-lg bg-stone-50 hover:bg-stone-100 text-stone-600 text-xs font-medium transition-colors text-center"
                      >
                        Download
                      </a>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Bulk Transfer Company Modal */}
      {bulkTransferOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm" onClick={() => setBulkTransferOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
              <div>
                <h2 className="text-lg font-bold text-stone-900">Transfer Customers</h2>
                <p className="text-stone-400 text-xs mt-0.5">Move {selectedCustomerIds.size} selected customer{selectedCustomerIds.size > 1 ? 's' : ''} to another company</p>
              </div>
              <button
                onClick={() => setBulkTransferOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {bulkTransferError && (
                <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg px-4 py-3 flex items-center gap-2">
                  <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center" />
                  {bulkTransferError}
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">Target Company *</label>
                <select
                  value={bulkTargetCompanyId}
                  onChange={(e) => setBulkTargetCompanyId(e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
                >
                  <option value="">Choose a company...</option>
                  {allCompaniesList.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <p className="text-stone-400 text-xs leading-relaxed">
                <i className="ri-information-line text-brand-600 mr-1" />
                This will reassign all selected customers to the chosen company. Their documents and KYC history will remain intact.
              </p>
              <div className="pt-2 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setBulkTransferOpen(false)}
                  className="px-4 py-2.5 rounded-full text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleBulkTransfer}
                  disabled={!bulkTargetCompanyId || bulkTransferLoading || String(bulkTargetCompanyId) === String(companyId)}
                  className="bg-brand-600 hover:bg-brand-700 disabled:bg-stone-300 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors flex items-center gap-2 whitespace-nowrap"
                >
                  {bulkTransferLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Transferring...
                    </>
                  ) : (
                    <>
                      <i className="ri-exchange-line" />
                      Transfer {selectedCustomerIds.size} Customer{selectedCustomerIds.size > 1 ? 's' : ''}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <DocumentDetailModal
        isOpen={!!detailDoc}
        onClose={() => setDetailDoc(null)}
        documentId={detailDoc ?? null}
      />

      <AddUserModal
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        onSuccess={() => {
          setAddUserOpen(false);
          refetchUsers();
        }}
      />

      <EditUserModal
        open={!!editingUser}
        user={editingUser}
        onClose={() => setEditingUser(null)}
        onSuccess={() => {
          setEditingUser(null);
          refetchUsers();
        }}
      />

      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={deleteTarget?.mode === 'hard' ? handleHardDelete : handleSoftDelete}
        title={deleteTarget?.mode === 'hard' ? 'Permanent Delete' : 'Deactivate Customer'}
        entityName={deleteTarget?.name}
        description={
          deleteTarget?.mode === 'hard'
            ? 'This will permanently erase the customer profile, all KYC requests, and every uploaded document from disk. This action cannot be undone.'
            : 'The customer profile will be disabled and hidden from the list. You can reactivate it later.'
        }
      />

      <DeleteConfirmModal
        isOpen={!!userDeleteTarget}
        onClose={() => setUserDeleteTarget(null)}
        onConfirm={userDeleteTarget?.mode === 'hard' ? handleUserHardDelete : handleUserSoftDelete}
        title={userDeleteTarget?.mode === 'hard' ? 'Permanent Delete' : 'Deactivate User'}
        entityName={userDeleteTarget?.name}
        description={
          userDeleteTarget?.mode === 'hard'
            ? 'This will permanently erase the user account and their avatar. KYC requests and documents will remain. This action cannot be undone.'
            : 'The user account will be disabled and hidden from the list. You can reactivate it later.'
        }
      />
    </div>
  );
}